import { LitElement, html } from "lit";

import { customElement, property } from "lit/decorators.js";

import type { Post } from "api-contracts/rpc/posts-query";
import { DataQueryController } from "ui-common/data-query-controller";
import "ui-components/post-card/post-card";

import { postsSelectionGridStyles } from "./posts-selection-grid.css";

import { postsQueryOptions } from "../../queries/posts";

@customElement("posts-selection-grid")
export class PostsSelectionGrid extends LitElement {
  static override styles = [postsSelectionGridStyles];

  @property({ type: Array })
  selectedPostIds: string[] = [];

  private postsController = new DataQueryController(this, () => postsQueryOptions);

  private get posts(): Post[] {
    const { data } = this.postsController.result;
    if (!data?.posts) {
      return [];
    }

    // Return all posts - no filtering needed
    return data.posts;
  }

  private get isLoading(): boolean {
    return this.postsController.result.isPending;
  }

  private get hasError(): boolean {
    return this.postsController.result.isError;
  }

  private get selectedCount(): number {
    return this.selectedPostIds.length;
  }

  private handlePostToggle = (postId: string) => {
    const isSelected = this.selectedPostIds.includes(postId);
    let newSelectedIds: string[];

    if (isSelected) {
      newSelectedIds = this.selectedPostIds.filter((id) => id !== postId);
    } else {
      newSelectedIds = [...this.selectedPostIds, postId];
    }

    this.dispatchEvent(
      new CustomEvent("posts-selection-change", {
        bubbles: true,
        composed: true,
        detail: {
          selectedPostIds: newSelectedIds,
        },
      }),
    );
  };

  private handleRetry = () => {
    this.postsController.refetch();
  };

  private renderPostCard(post: Post) {
    const isSelected = this.selectedPostIds.includes(post.id);

    return html`
      <div class="post-card-container ${isSelected ? "selected" : ""}" @click=${() => this.handlePostToggle(post.id)}>
        <post-card
          ?hideDeleteButton=${true}
          .id=${post.id}
          .teaser=${post.teaser || ""}
          .mediaUrl=${post.mediaUrl || ""}
          .status=${post.status}
        ></post-card>
        <div class="checkbox-overlay">
          <div class="checkbox ${isSelected ? "checked" : ""}">${isSelected ? this.renderCheckMark() : ""}</div>
        </div>
      </div>
    `;
  }

  private renderCheckMark() {
    return html`
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          opacity="0.2"
          d="M14.0001 8.00007C14.0001 9.18678 13.6483 10.3468 12.989 11.3335C12.3297 12.3202 11.3926 13.0893 10.2962 13.5434C9.19983 13.9975 7.99342 14.1164 6.82952 13.8849C5.66562 13.6533 4.59651 13.0819 3.75738 12.2428C2.91826 11.4036 2.34681 10.3345 2.11529 9.17063C1.88378 8.00673 2.0026 6.80031 2.45673 5.70394C2.91086 4.60757 3.67991 3.67049 4.66661 3.01119C5.65332 2.3519 6.81337 2 8.00008 2C9.59139 2 11.1175 2.63215 12.2428 3.75738C13.368 4.88261 14.0001 6.40875 14.0001 8.00007Z"
          fill="#CFF7D3"
        />
        <path
          d="M10.8539 6.14631C10.9004 6.19274 10.9372 6.24789 10.9624 6.30859C10.9876 6.36929 11.0005 6.43435 11.0005 6.50006C11.0005 6.56577 10.9876 6.63083 10.9624 6.69153C10.9372 6.75223 10.9004 6.80738 10.8539 6.85382L7.35382 10.3539C7.30739 10.4003 7.25224 10.4372 7.19154 10.4624C7.13084 10.4876 7.06578 10.5005 7.00007 10.5005C6.93436 10.5005 6.8693 10.4876 6.8086 10.4624C6.7479 10.4372 6.69275 10.4003 6.64632 10.3539L5.1463 8.85384C5.05248 8.76002 4.99977 8.63277 4.99977 8.50008C4.99977 8.3674 5.05248 8.24015 5.1463 8.14633C5.24012 8.05251 5.36737 7.9998 5.50005 7.9998C5.63274 7.9998 5.75998 8.05251 5.85381 8.14633L7.00007 9.29322L10.1464 6.14631C10.1928 6.09982 10.2479 6.06294 10.3086 6.03778C10.3693 6.01261 10.4344 5.99966 10.5001 5.99966C10.5658 5.99966 10.6309 6.01261 10.6916 6.03778C10.7523 6.06294 10.8074 6.09982 10.8539 6.14631ZM14.5002 8.00008C14.5002 9.28567 14.1189 10.5424 13.4047 11.6113C12.6905 12.6803 11.6753 13.5134 10.4876 14.0054C9.29982 14.4973 7.99287 14.6261 6.73198 14.3753C5.47109 14.1245 4.31288 13.5054 3.40383 12.5963C2.49478 11.6873 1.87571 10.5291 1.6249 9.26818C1.37409 8.00729 1.50282 6.70034 1.99479 5.51261C2.48677 4.32487 3.3199 3.3097 4.38883 2.59546C5.45776 1.88122 6.71449 1.5 8.00008 1.5C9.72345 1.50182 11.3757 2.18723 12.5943 3.40584C13.8129 4.62445 14.4983 6.27671 14.5002 8.00008ZM13.5001 8.00008C13.5001 6.91227 13.1776 5.84889 12.5732 4.94441C11.9689 4.03992 11.1099 3.33497 10.1049 2.91868C9.09986 2.50239 7.99398 2.39347 6.92707 2.60569C5.86016 2.81792 4.88015 3.34175 4.11095 4.11094C3.34175 4.88014 2.81792 5.86016 2.6057 6.92707C2.39348 7.99398 2.5024 9.09986 2.91868 10.1049C3.33497 11.1099 4.03993 11.9689 4.94441 12.5732C5.84889 13.1776 6.91227 13.5001 8.00008 13.5001C9.45828 13.4985 10.8563 12.9185 11.8874 11.8874C12.9185 10.8563 13.4985 9.45828 13.5001 8.00008Z"
          fill="#CFF7D3"
        />
      </svg>
    `;
  }

  private renderLoadingState() {
    return html`
      <div class="loading-state">
        <div class="loading-grid">
          ${Array.from(
            { length: 6 },
            () => html`
              <div class="skeleton-card">
                <div class="skeleton-thumbnail"></div>
              </div>
            `,
          )}
        </div>
      </div>
    `;
  }

  private renderErrorState() {
    return html`
      <div class="error-state">
        <p class="error-message">Failed to load posts</p>
        <button class="retry-button" @click=${this.handleRetry}>Retry</button>
      </div>
    `;
  }

  private renderEmptyState() {
    return html`
      <div class="empty-state">
        <p class="empty-message">No posts available</p>
        <p class="empty-description">Create your first post to get started</p>
      </div>
    `;
  }

  protected override render() {
    if (this.isLoading) {
      return this.renderLoadingState();
    }

    if (this.hasError) {
      return this.renderErrorState();
    }

    if (this.posts.length === 0) {
      return this.renderEmptyState();
    }

    return html`
      <div class="posts-selection-container">
        <div class="posts-grid">${this.posts.map((post) => this.renderPostCard(post))}</div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "posts-selection-grid": PostsSelectionGrid;
  }
}
